// =============================================
// aftermarketService — automatyczne daty wygasniecia domen z AfterMarket.pl
//
// Zastepuje reczne klepanie `websites.expiry_date` w formularzu. Jedno
// wywolanie GET /domain/list zwraca cale konto (name + expires + autorenew),
// wiec sync to: pobierz liste -> dopasuj po znormalizowanej nazwie -> zapisz.
//
// TWARDA ZASADA KONT (nie zmieniac bez decyzji wlasciciela):
//   workspace='firma'        -> konto AfterMarket z kluczami *_FIRMA (albo legacy
//                               AFTERMARKET_PUBLIC_KEY/SECRET_KEY) w Coolify.
//   workspace='sideprojects' -> Sponsoraki, INNE konto AfterMarket, klucze
//                               *_SPONSORAKI w Coolify (od 2026-09-22 sync dat
//                               dziala tez dla nich).
//   Sync biegnie ZAWSZE dla JEDNEGO konta naraz: naglowek X-Workspace wybiera
//   klucz w nginx, a buildSyncPlan() dopuszcza tylko wiersze z TEGO workspace.
//   Filtr jest TUTAJ, w serwisie, a nie w UI — zeby zaden przycisk nie mogl
//   przepisac dat firmowych z konta Sponsorakow ani odwrotnie.
//
// Auth: nginx wstrzykuje `Authorization: Basic base64(public:secret)` —
// klient NIE wysyla zadnych kluczy (patrz nginx.conf /api/aftermarket/).
//
// PULAPKI API (potwierdzone na produkcji w innym projekcie):
//   * `expires` to unix timestamp w SEKUNDACH — trzeba * 1000
//   * blad przychodzi jako {ok:false,error:"..."} z HTTP 200 — sam status nie wystarczy
//   * domena znika z /domain/list gdy wygasnie i przepadnie -> oznaczamy
//     ja `not_on_account` zamiast kasowac date
// =============================================
import { supabase } from "@/integrations/supabase/client";
import type { Website } from "@/hooks/useWebsites";
import { logError, logPublic } from "@/services/contentEngine/contentLogger";

const BASE = "/api/aftermarket";
const PAGE_SIZE = 5000; // max dopuszczalny przez API
const REQUEST_TIMEOUT_MS = 90_000;
const UPSERT_CHUNK = 100;

export type AmWorkspace = "firma" | "sideprojects";

export interface AmDomain {
  name: string;
  expires?: number; // unix timestamp w SEKUNDACH
  autorenew?: boolean;
  autorenewDays?: number;
}

export type ExpirySource = "manual" | "aftermarket" | "not_on_account";

export interface DomainSyncChange {
  id: string;
  name: string;
  oldDate: string | null;
  newDate: string | null;
  autorenew?: boolean;
}

export interface DomainSyncReport {
  /** Data faktycznie sie zmienila. */
  updated: DomainSyncChange[];
  /** Domena znaleziona na koncie, data bez zmian. */
  unchanged: DomainSyncChange[];
  /** W panelu jest, na koncie AfterMarket NIE MA (przepadla / inny rejestrator). */
  notOnAccount: DomainSyncChange[];
  /** Na koncie jest, w panelu nie ma — tylko do wiadomosci, nic nie zapisujemy. */
  extraOnAccount: string[];
  /** Ile domen z INNEGO workspace pominieto (sa na innym koncie AfterMarket). */
  skippedOtherWorkspace: number;
  /** Ile pozycji zwrocilo API. */
  accountTotal: number;
  /** Konto, ktorego dotyczy raport. */
  workspace: AmWorkspace;
}

// ---------------------------------------------------------------------------
// Czyste funkcje (testowalne bez sieci)
// ---------------------------------------------------------------------------

/**
 * Sprowadza domene do postaci porownywalnej z `name` z API AfterMarket.
 * W bazie url wyglada np. "https://www.40000malarzy.pl/", a API zwraca
 * "40000malarzy.pl" — bez tego dopasowanie by nie trafilo w nic.
 */
export function normalizeDomain(input?: string | null): string {
  if (!input) return "";
  let s = String(input).trim().toLowerCase();
  if (!s || s === "empty") return "";
  s = s.replace(/^[a-z][a-z0-9+.-]*:\/\//, ""); // protokol
  s = s.replace(/^[^@/]*@/, ""); // ewentualny user:pass@
  s = s.split(/[/?#]/)[0]; // sciezka / query / hash
  s = s.split(":")[0]; // port
  s = s.replace(/^www\./, "");
  s = s.replace(/\.$/, ""); // root dot
  return s;
}

/** Klucz dopasowania dla wiersza z panelu — url ma pierwszenstwo, name to fallback. */
export function websiteDomainKey(w: Pick<Website, "url" | "name">): string {
  return normalizeDomain(w.url) || normalizeDomain(w.name);
}

/** Unix timestamp (SEKUNDY) -> "YYYY-MM-DD". Null gdy brak / smiec. */
export function parseExpiry(unixTs?: number | null): string | null {
  if (typeof unixTs !== "number" || !Number.isFinite(unixTs) || unixTs <= 0) return null;
  const d = new Date(unixTs * 1000);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Czy wiersz nalezy do konta, ktore wlasnie synchronizujemy (default DB = firma). */
export function isSyncable(w: Pick<Website, "workspace">, workspace: AmWorkspace = "firma"): boolean {
  return (w.workspace || "firma") === workspace;
}

/** Porownanie dat odporne na "EMPTY" i puste stringi zapisywane przez stary formularz. */
function sameDate(a?: string | null, b?: string | null): boolean {
  const norm = (v?: string | null) => {
    const s = (v || "").trim();
    return !s || s.toUpperCase() === "EMPTY" ? "" : s;
  };
  return norm(a) === norm(b);
}

// ---------------------------------------------------------------------------
// Sieć
// ---------------------------------------------------------------------------

export class AftermarketError extends Error {}

export const WORKSPACE_LABEL: Record<AmWorkspace, string> = { firma: "Firma", sideprojects: "Sponsoraki" };
const WORKSPACE_ENV: Record<AmWorkspace, string> = {
  firma: "AFTERMARKET_PUBLIC_KEY_FIRMA / AFTERMARKET_SECRET_KEY_FIRMA (albo legacy AFTERMARKET_PUBLIC_KEY / _SECRET_KEY)",
  sideprojects: "AFTERMARKET_PUBLIC_KEY_SPONSORAKI / AFTERMARKET_SECRET_KEY_SPONSORAKI",
};

async function amGet(path: string, workspace: AmWorkspace = "firma"): Promise<any> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  const label = WORKSPACE_LABEL[workspace];
  try {
    // X-Workspace wybiera klucz konta AfterMarket w nginx (firma → _FIRMA,
    // sideprojects → _SPONSORAKI). Zawsze wysylamy jawnie, zeby sync dat i
    // zmiana DNS szly przez to samo konto co wybrany workspace.
    const headers: Record<string, string> = { Accept: "application/json", "X-Workspace": workspace };
    const res = await fetch(`${BASE}${path}`, {
      method: "GET",
      headers,
      signal: controller.signal,
    });

    if (res.status === 401 || res.status === 403) {
      throw new AftermarketError(
        `AfterMarket odrzucil klucze konta ${label} (HTTP ${res.status}). Sprawdz w Coolify: ${WORKSPACE_ENV[workspace]}.`,
      );
    }

    // 502/503/504 przychodzi z nginx albo wprost od AfterMarket i ZAWSZE znaczy
    // to samo: ich API jest chwilowo niedostepne. Rozroznienie jest istotne, bo
    // wczesniejsza wersja komunikatu sugerowala brak proxy i wysylala na falszywy trop.
    if (res.status === 502 || res.status === 503 || res.status === 504) {
      throw new AftermarketError(
        `AfterMarket nie odpowiada (HTTP ${res.status}) — ich API jest chwilowo niedostepne. ` +
          `Panel jest sprawny, sprobuj za kilka minut. Szybki test: otworz https://json.aftermarket.pl/__docs/ w przegladarce.`,
      );
    }

    let json: any = null;
    try {
      json = await res.json();
    } catch {
      throw new AftermarketError(
        `AfterMarket zwrocil odpowiedz nie-JSON (HTTP ${res.status}). Jesli to strona HTML — sprawdz, ` +
          `czy proxy /api/aftermarket/ jest wdrozone (blok w nginx.conf).`,
      );
    }

    // API sygnalizuje blad w ciele, nie statusem HTTP. Pusty klucz konta
    // (np. _SPONSORAKI nie wpisane w Coolify) tez konczy sie tutaj — dopisujemy
    // podpowiedz, zeby nie szukac winy w panelu.
    if (!json?.ok) {
      const msg = String(json?.error ?? `nieznany blad (HTTP ${res.status})`);
      const authLike = /auth|key|klucz|login|credential|unauthori|permission|access/i.test(msg);
      throw new AftermarketError(
        `AfterMarket API (konto ${label}): ${msg}` +
          (authLike ? ` — sprawdz w Coolify: ${WORKSPACE_ENV[workspace]}.` : ""),
      );
    }
    return json;
  } catch (err) {
    if (err instanceof AftermarketError) throw err;
    if ((err as Error)?.name === "AbortError") {
      throw new AftermarketError("AfterMarket nie odpowiedzial w 90 s — sprobuj ponownie.");
    }
    throw new AftermarketError(`Polaczenie z AfterMarket nieudane: ${(err as Error)?.message || err}`);
  } finally {
    clearTimeout(timer);
  }
}

/** Pobiera WSZYSTKIE domeny z konta wskazanego workspace (paginacja co 5000). */
export async function fetchAmDomains(
  workspace: AmWorkspace = "firma",
  onPage?: (loaded: number) => void,
): Promise<AmDomain[]> {
  const all: AmDomain[] = [];
  let start = 0;

  // Twardy limit petli — gdyby API kiedys przestalo skracac ostatnia strone.
  for (let guard = 0; guard < 50; guard++) {
    const json = await amGet(`/domain/list?size=${PAGE_SIZE}&start=${start}&order=expires`, workspace);
    const items: AmDomain[] = Array.isArray(json.data) ? json.data : [];
    all.push(...items);
    onPage?.(all.length);
    if (items.length < PAGE_SIZE) break;
    start += PAGE_SIZE;
  }

  return all;
}

// ---------------------------------------------------------------------------
// Sync
// ---------------------------------------------------------------------------

/**
 * Buduje raport bez dotykania bazy — wydzielone z syncDomainExpiry, zeby dalo
 * sie przetestowac dopasowanie i filtr workspace bez sieci.
 */
export function buildSyncPlan(
  websites: Website[],
  amDomains: AmDomain[],
  workspace: AmWorkspace = "firma",
): {
  report: DomainSyncReport;
  writes: Array<{ website: Website; date: string | null; source: ExpirySource; autorenew: boolean | null }>;
} {
  const byName = new Map<string, AmDomain>();
  for (const d of amDomains) {
    const key = normalizeDomain(d.name);
    if (key) byName.set(key, d);
  }

  const report: DomainSyncReport = {
    updated: [],
    unchanged: [],
    notOnAccount: [],
    extraOnAccount: [],
    skippedOtherWorkspace: 0,
    accountTotal: amDomains.length,
    workspace,
  };
  const writes: Array<{ website: Website; date: string | null; source: ExpirySource; autorenew: boolean | null }> = [];
  const matchedKeys = new Set<string>();

  for (const w of websites) {
    // Wiersz z innego workspace = inne konto AfterMarket. Nawet gdyby nazwa
    // domeny pokrywala sie z lista z API, NIE dotykamy go.
    if (!isSyncable(w, workspace)) {
      report.skippedOtherWorkspace++;
      continue;
    }

    const key = websiteDomainKey(w);
    const am = key ? byName.get(key) : undefined;

    if (!am) {
      report.notOnAccount.push({ id: w.id, name: w.name, oldDate: w.expiry_date ?? null, newDate: w.expiry_date ?? null });
      // Date ZOSTAWIAMY — brak na koncie nie znaczy, ze poprzednia byla zla.
      writes.push({ website: w, date: null, source: "not_on_account", autorenew: null });
      continue;
    }

    matchedKeys.add(key);
    const newDate = parseExpiry(am.expires);
    const change: DomainSyncChange = {
      id: w.id,
      name: w.name,
      oldDate: w.expiry_date ?? null,
      newDate,
      autorenew: am.autorenew,
    };

    if (sameDate(w.expiry_date, newDate)) report.unchanged.push(change);
    else report.updated.push(change);

    writes.push({ website: w, date: newDate, source: "aftermarket", autorenew: am.autorenew ?? null });
  }

  for (const [key] of byName) {
    if (!matchedKeys.has(key)) report.extraOnAccount.push(key);
  }
  report.extraOnAccount.sort();

  return { report, writes };
}

/**
 * Pelny przebieg: API -> dopasowanie -> zapis do bazy.
 * Zapisuje `expiry_date` tylko dla domen znalezionych na koncie; dla reszty
 * ustawia sam znacznik `expiry_source='not_on_account'`.
 */
export async function syncDomainExpiry(
  websites: Website[],
  workspace: AmWorkspace = "firma",
  onProgress?: (phase: "fetch" | "save", done: number, total: number) => void,
): Promise<DomainSyncReport> {
  const label = WORKSPACE_LABEL[workspace];
  const amDomains = await fetchAmDomains(workspace, (loaded) => onProgress?.("fetch", loaded, loaded));
  logPublic("AfterMarket", `[${label}] Pobrano ${amDomains.length} domen z konta`);

  const { report, writes } = buildSyncPlan(websites, amDomains, workspace);
  const syncedAt = new Date().toISOString();

  const rows = writes.map(({ website, date, source, autorenew }) => ({
    // PK + kolumny NOT NULL musza byc w upsercie, inaczej PostgREST odrzuci wiersz.
    id: website.id,
    user_id: website.user_id,
    name: website.name,
    url: website.url,
    // dla not_on_account zostawiamy poprzednia date nietknieta
    expiry_date: source === "aftermarket" ? date : website.expiry_date,
    expiry_synced_at: syncedAt,
    expiry_source: source,
    am_autorenew: autorenew,
  }));

  let saved = 0;
  for (let i = 0; i < rows.length; i += UPSERT_CHUNK) {
    const chunk = rows.slice(i, i + UPSERT_CHUNK);
    const { error } = await (supabase as any).from("websites").upsert(chunk, { onConflict: "id" });
    if (error) {
      logError("AfterMarket", `[${label}] Zapis paczki ${i / UPSERT_CHUNK + 1} nieudany: ${error.message}`);
      throw new AftermarketError(`Nie udalo sie zapisac dat: ${error.message}`);
    }
    saved += chunk.length;
    onProgress?.("save", saved, rows.length);
  }

  logPublic(
    "AfterMarket",
    `[${label}] Sync zakonczony — zmienione: ${report.updated.length}, bez zmian: ${report.unchanged.length}, ` +
      `brak na koncie: ${report.notOnAccount.length}, pominiete z innego workspace: ${report.skippedOtherWorkspace}`,
  );

  return report;
}

// ---------------------------------------------------------------------------
// Nameservery (/cloudflare: kreator "Przenies domeny" + "Ustaw NS" w Domeny w CF)
// ---------------------------------------------------------------------------

/**
 * Buduje query string. UWAGA: tablice ("array of strings") AfterMarket przyjmuje
 * jako JEDEN parametr z wartosciami po przecinku (`ns=a,b`) — tak mowi ich
 * dokumentacja (__docs/?page=parameters). Konwencja PHP `ns[]=a&ns[]=b` byla
 * odrzucana jako "Nieprawidlowe serwery nazw" (potwierdzone 2026-10-07: te same
 * NS przez formularz AfterMarket przechodzily, przez API nie).
 */
export function amQuery(params: Record<string, string | string[]>): string {
  return Object.entries(params)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(Array.isArray(v) ? v.join(",") : v)}`)
    .join("&");
}

/** Obecne nameservery domeny (/domain/ns/get). Zwraca liste NS albo []. */
export async function nsGet(name: string, workspace: AmWorkspace = "firma"): Promise<string[]> {
  const domain = normalizeDomain(name);
  if (!domain) throw new AftermarketError("Podaj poprawna domene.");
  const json = await amGet(`/domain/ns/get?${amQuery({ name: domain })}`, workspace);
  const data = json?.data;
  if (Array.isArray(data)) return data.filter((x) => typeof x === "string" && x.trim());
  return [];
}

/**
 * Ustawia nameservery domeny (/domain/ns/set). OPERACJA ZAPISU — zly NS =
 * domena offline, dlatego UI wymaga podgladu ns/get + potwierdzenia.
 * Zwraca true gdy AfterMarket potwierdzil.
 */
export async function nsSet(name: string, ns: string[], workspace: AmWorkspace = "firma"): Promise<boolean> {
  const domain = normalizeDomain(name);
  if (!domain) throw new AftermarketError("Podaj poprawna domene.");
  const clean = ns.map((n) => n.trim().toLowerCase()).filter(Boolean);
  if (clean.length < 2) throw new AftermarketError("Podaj co najmniej 2 nameservery.");
  const json = await amGet(`/domain/ns/set?${amQuery({ name: domain, ns: clean })}`, workspace);
  // ns/set zwraca boolean w json.data; json.ok=true oznacza samo powodzenie wywolania.
  return json?.data === true || json?.data === "true" || json?.ok === true;
}
